import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import Button from 'modules/views/shared/Button';
import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import WithBranding from 'modules/views/shared/WithBranding';
import { ReactComponent as ResetPasswordTickIcon } from 'assets/icons/ResetPasswordTick.svg';

import resetPasswordSchema from './schema';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useResetPassword, useSendOtp } from './hooks';
import { useStyles } from './styles';
import { ROUTES } from 'constants/routes';
import { extractNationalNumber } from 'utils';
import { useAppSelector } from 'modules/state-mgmt/store';

const ForgotPassword = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const { isAdmin } = useAppSelector(state => ({
    isAdmin: state.user?.isAdmin,
  }));

  const { isLoading: isResetPasswordLoading, resetPassword } = useResetPassword({
    onSuccess: () => {
      setIsSuccess(true);
    },
    onFailure: backendErrors => {
      setErrors(backendErrors);
    },
  });

  const { isLoading: isSendOtpLoading, sendOtp } = useSendOtp({
    onSuccess: (email, mobilePhoneNumber) => {
      navigate(ROUTES.VERIFY_OTP.path, { state: { email, mobilePhoneNumber } });
    },
    onFailure: backendErrors => {
      setErrors(backendErrors);
    },
  });

  const isLoading = isResetPasswordLoading || isSendOtpLoading;

  const onSubmit = (values: any) => {
    const { email, mobilePhoneNumber } = values;

    const hasEmail = email && email.trim().length > 0;
    const nationalNumber = extractNationalNumber(mobilePhoneNumber);
    const hasMobile = nationalNumber && nationalNumber.trim().length > 0;

    // Send both values if provided
    const emailToSend = hasEmail ? email.trim() : undefined;
    const phoneToSend = hasMobile ? mobilePhoneNumber : undefined;

    // In Admin Mode the worker cannot open an emailed reset link on the admin's device,
    // so they get a one-time password instead (CH-4506). The access-PIN API takes a single
    // channel, and the PIN can only be verified against the channel it was sent to, so pick
    // one here (mobile wins when both are filled in) and carry just that one forward.
    if (isAdmin) {
      sendOtp(phoneToSend ? undefined : emailToSend, phoneToSend);
      return;
    }

    resetPassword(emailToSend, phoneToSend);
  };

  const { values, errors, handleSubmit, setValue, setErrors } = useForm({
    initialValues: {
      email: '',
      mobilePhoneNumber: '',
    },
    validationSchema: resetPasswordSchema(t),
    onSubmit: onSubmit,
  });

  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  const SuccessContent = (
    <div className={classes.successContainer}>
      <ResetPasswordTickIcon className={classes.successIcon} />
      <div className={classes.successTitle}>{t('resetPassword.success.title')}</div>
      <Button
        label={t('resetPassword.buttons.backToSignIn')}
        hasEndArrow={true}
        styleClass={classes.successButton}
        testId="forgot-password-back-to-signin-button"
        onClick={() => navigate(ROUTES.LOGIN.path)}
      />
    </div>
  );

  const FormContent = (
    <>
      <ContentHeader title={t('resetPassword.title')} subtitle={t('resetPassword.subtitle')} />

      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid item xs={12}>
          <TextField
            label={t('resetPassword.email')}
            name="email"
            placeholder={t('resetPassword.email')}
            value={values.email}
            error={errors.email?.message}
            testId="email-input"
            onChange={e => setValue('email', e.target.value?.trim())}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'one-time-code' : 'off'}
          />
        </Grid>

        <Grid justifyItems={'center'} item xs={12}>
          <div className={classes.orText}>{t('resetPassword.or')}</div>
        </Grid>

        <Grid item xs={12}>
          <MobileInput
            label={t('resetPassword.phoneNumber')}
            placeholder={t('resetPassword.phoneNumber')}
            value={values.mobilePhoneNumber}
            error={errors.mobilePhoneNumber?.message}
            testId="mobilePhoneNumber-input"
            onChange={({ phone }) => setValue('mobilePhoneNumber', phone)}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'one-time-code' : 'off'}
          />
        </Grid>
      </Grid>

      <div className={classes.buttonParent}>
        <Button
          label={t('resetPassword.buttons.backToLogin')}
          styleClass={classes.backButton}
          onClick={() => navigate(ROUTES.LOGIN.path)}
          disabled={isLoading}
          variant="text"
          testId="back-to-login-button"
        />
        <Button
          label={t(isAdmin ? 'resetPassword.buttons.sendOneTimePassword' : 'resetPassword.buttons.sendResetLink')}
          hasEndArrow={true}
          isLoading={isLoading}
          styleClass={classes.button}
          testId="send-reset-link-button"
          onClick={handleSubmit}
        />
      </div>
    </>
  );

  const Content = isSuccess ? SuccessContent : FormContent;

  return <WithBranding Content={Content} />;
};

export default memo(ForgotPassword);
